import React from 'react';
import _ from 'lodash';

// Components
import Button from 'ui/button';
import { Loader } from 'ui/loader';
import TextField from 'ui/form/text-field';
import ListElement from './list-element';
import { FormField } from 'ui/form/form-field';
import { renderIcon } from '../utils.js';
import { ICON_CLOSE } from 'icons';
import ChipsList from 'ui/form/chips';

// Props
import { AutoCompleteFieldProps } from 'ui/form/fields';

// Styles
import styles from './auto-complete.scss';
import { REQUIRED } from 'ui/form/constants';
import { isValue } from '../utils';

// Key bindings
const KEYS = [13, 27, 38, 40, 9, 8];
const KEY_ENTER = 13;
const KEY_ESC = 27;
const KEY_ARROW_UP = 38;
const KEY_ARROW_DOWN = 40;
const KEY_TAB = 9;
const KEY_BACKSPACE = 8;

interface AutoCompleteState {
  hasFocus: boolean;
  highlight: number;
  showList: boolean;
  dataSet: [];
}

// Converts a data set to a array of objects
const convertDataSet = dataSet => {
  if (!dataSet) return dataSet;
  let objDataSet = [];
  for (let i = 0; i < dataSet.length; i++) {
    objDataSet.push({ text: dataSet[i] });
  }
  return objDataSet;
};

export default class AutoComplete extends FormField<AutoCompleteFieldProps, AutoCompleteState> {
  /**
   * allowCustomValue: the field can be anything. Default is that the item have to be in the dataSet
   * min && max: for multiSelect, you have to choose at least/max x items.
   */
  static getErrors(props: AutoCompleteFieldProps, formData) {
    const value = isValue(props.value) ? props.value : (formData || {})[props.name];
    const { required, multiSelect, min, max } = props;

    if (required && !value) return REQUIRED;
    if (multiSelect) {
      if (!value) return 'Select at least ' + min + ' items';
      if (min && value.length < min) return 'Select at least ' + min + ' items';
      if (max && value.length > max) return 'You can only select ' + max + ' items';
    }

    return false;
  }

  static getDerivedStateFromProps(
    { dataSet, value, stringArray },
    { prevValue, fetchedDataSet, prevDataSet, textFieldValue, textFieldImage }
  ) {
    // if dataSet is a function: fetch data set
    if (_.isFunction(dataSet)) dataSet = fetchedDataSet;
    // if the dataSet is a string array: convert it to objects
    if (!_.isObject(dataSet[0])) {
      dataSet = convertDataSet(dataSet);
      stringArray = true;
    }
    if (!_.isEqual(value, prevValue) || !_.isEqual(prevDataSet, dataSet)) {
      if (dataSet) {
        const d = dataSet.find(d => d.value === value);
        if (d) {
          textFieldValue = d.text || d.value || value;
          textFieldImage = d.image;
        }
      }
      const state = {
        textFieldValue,
        textFieldImage,
        prevValue: value,
        prevDataSet: dataSet,
        stringArray: stringArray
      };
      return state;
    }
    return null;
  }

  static defaultProps = {
    minAutoComplete: 0
  };

  state = {
    highlight: -1,
    fetchedDataSet: [],
    showList: true,
    textFieldValue: '',
    textFieldImage: ''
  };

  list = [];

  componentDidMount() {
    if (_.isFunction(this.props.dataSet)) this.fetch();
  }

  componentDidUpdate() {
    // Make sure list holder is place directly under the input field
    if (this.listHolder && this.textField && this.holder) {
      const inputRect = this.textField.input.getBoundingClientRect();
      const holderRect = this.holder.getBoundingClientRect();
      let top = inputRect.bottom - holderRect.top;
      this.listHolder.style.top = top + 'px';
    }
  }

  validate() {
    const errors = AutoComplete.getErrors(this.props);
    this.setState({ errors });

    return !errors;
  }

  get dataSet() {
    const { prevDataSet } = this.state;
    if (_.isFunction(prevDataSet)) return this.state.fetchedDataSet;
    return prevDataSet || [];
  }

  getDataSet(): [] {
    return this.dataSet.filter(this.filterList);
  }

  /**
   * Mapping for the data set to keep track of values
   */
  getMapping() {
    const { mapping = {} } = this.props;
    return {
      faded: 'faded',
      image: 'image',
      text: 'text',
      ...mapping
    };
  }

  /**
   * Checks if item exists in array
   */
  isSelected = i => {
    const mapping = this.getMapping();
    if (this.state.stringArray) return this.props.value.includes(i.text);
    return this.props.value
      .map(function(e) {
        return e[mapping.text];
      })
      .includes(i[mapping.text] || i);
  };

  /**
   * Right clear button
   */
  clear = () => {
    const { onChange, onSelect } = this.props;
    if (onChange) onChange();
    if (onSelect) onSelect();
    this.setState({ highlight: -1, textFieldValue: '', textFieldImage: '' });
  };

  /**
   * If dataSet is a function
   */
  async fetch(query: string) {
    this.setState({ loading: true });
    const fetchedDataSet = await this.props.dataSet(query);
    this.setState({ fetchedDataSet, loading: false });
  }

  filterList = item => {
    const { textFieldValue } = this.state;
    const mapping = this.getMapping();
    let testString = _.isString(item)
      ? item
      : `${item[mapping.text] || ''} ${item[mapping.faded] || ''}`;
    // Try catch to catch invalid regular expression error
    // if user puts something stupid into the field
    try {
      let sExp = new RegExp((textFieldValue || '').split('').join('\\s*'), 'i');
      return sExp.test(testString);
    } catch (e) {
      console.log('INVALID TEXT FIELD VALUE', e);
      return false;
    }
  };

  handleTextChange = textFieldValue => {
    const { onChange, multiSelect } = this.props;
    let { textFieldImage } = this.state;
    if (onChange && !multiSelect) {
      const item = this.dataSet.find(item => textFieldValue === item.text);
      if (item) {
        this.setState({ errors: false });
        onChange(_.isString(item) ? item : item.text);
      } else {
        onChange(this.props.allowCustomValue ? textFieldValue : '');
        textFieldImage = '';
      }
    }
    this.setState({ highlight: -1, showList: true, textFieldValue, textFieldImage });
  };

  handleSelect = selected => {
    const mapping = this.getMapping();
    const { onSelect, onChange, multiSelect, value } = this.props;
    let selectedValue = selected[mapping.text];
    let listArray;
    // If you can select multiple
    if (multiSelect) {
      listArray = (value || []).slice();
      selectedValue = '';
      // if the dataSet was originally a stringArray, return a stringArray to the form
      if (this.state.stringArray) listArray.push(selected.text);
      else listArray.push(selected);
      // if its not multiSelect: just change the item
    } else {
      if (this.state.stringArray) listArray = selectedValue;
      else listArray = selected;
    }
    this.setState(
      {
        highlight: -1,
        showList: false,
        prevValue: selected,
        errors: false,
        textFieldValue: selectedValue,
        textFieldImage: selected[mapping.image]
      },
      () => {
        if (onSelect) onSelect(listArray);
        else if (onChange) onChange(listArray);
      }
    );
  };

  handleBlur() {
    if (!this.props.noValidation) this.validate();
    this.setState({ hasFocus: false });
  }

  handleFocus() {
    this.setState({ hasFocus: true, highlight: -1, showList: true });
  }

  // Remove button for chips: MultiSelect only
  handleRemove = (value: any) => {
    let listArray = this.props.value;
    // If its a stringArray: do lodash pull
    if (this.state.stringArray) _.pull(listArray, value);
    // else its a object array: use filter to remove the item
    else {
      listArray = listArray.filter(function(obj) {
        return obj.text !== value;
      });
    }
    // If there are no items left: prevent empty arrays in form
    if (!listArray.length) this.props.onChange();
    else this.props.onChange(listArray);
  };

  onKeyDown = (a: KeyboardEvent) => {
    // Check if we want to handle the key pressed
    if (!KEYS.includes(a.keyCode)) return;

    const { maxResults, multiSelect, value, minAutoComplete } = this.props;
    let { highlight, showList, textFieldValue } = this.state;
    // if there is no dataSet, no data can be displayed in ListElement. return
    let dataSet = this.getDataSet();
    if (!dataSet) return;
    if (multiSelect) {
      // If multiSelect is enabled, filter the list to not show values that are already selected
      if (value) dataSet = dataSet.filter(i => !this.isSelected(i));
      // Deleting with MultiSelect: When backspace is pressed with no textFieldValue.
      // Delete the last item chosen
      if (a.keyCode === KEY_BACKSPACE && !textFieldValue) {
        const listArray = (value || []).slice();
        if (value && value.length && this.props.onChange) {
          listArray.pop();
          // if the list is empty: remove empty array from formData
          if (!listArray.length) this.props.onChange();
          else this.props.onChange(listArray);
        }
      }
    }
    // Check if the prop MinAutocomplete is set and the text is long enough to display the list
    if (minAutoComplete) {
      if ((textFieldValue && textFieldValue.length < minAutoComplete) || !textFieldValue) return;
    }

    // Prevents the user from auto selecting items by just spamming tab
    if ((!showList && a.keyCode === KEY_TAB) || (highlight === -1 && a.keyCode === KEY_TAB)) return;

    // Enables selecting items with ENTER and TAB.
    // Checks if the list is showing and that the item exists
    if (a.keyCode === KEY_ENTER || a.keyCode === KEY_TAB) {
      a.preventDefault();
      if (dataSet[highlight] && showList) this.handleSelect(dataSet[highlight]);

      // Enables opening the list with Arrow down and keeps track of higlighting
    } else if (a.keyCode === KEY_ARROW_DOWN) {
      a.preventDefault();
      if (!showList) {
        this.setState({ showList: true });
        return;
      }
      highlight += 1;

      // Highlighting with arrow up
    } else if (a.keyCode === KEY_ARROW_UP) {
      a.preventDefault();
      highlight -= 1;

      // Highlight represents an item in the dataSet. cannot be smaller than 0
      if (highlight < 0) {
        highlight = (maxResults && maxResults.length - 1) || (dataSet || []).length - 1;
      }

      // Close the list with Escape
    } else if (a.keyCode === KEY_ESC) {
      this.setState({ showList: false });
    }

    // Checks if highlight is over the dataSet length or maxResults.
    if (highlight >= (dataSet || []).length || highlight >= maxResults) highlight = 0;
    highlight = Math.max(highlight, -1);
    this.setState({ highlight });
  };

  renderList() {
    const { hideImages, maxResults, multiSelect, minAutoComplete, value } = this.props;
    const { highlight, loading, textFieldValue } = this.state;
    const mapping = this.getMapping();
    let list = this.getDataSet();
    if (multiSelect && value) list = list.filter(i => !this.isSelected(i));
    list = list.slice(0, maxResults);
    // Check if all the conditions are met to render the list
    if (minAutoComplete && textFieldValue && textFieldValue.length < minAutoComplete) return;
    else if (minAutoComplete && !textFieldValue) return;
    // If they are, return the list;
    else {
      return (
        <div className={styles.listHolder} ref={ref => (this.listHolder = ref)}>
          {!list.length && !loading && <h2 className="text-fade text-center">No results</h2>}
          {loading && <Loader />}
          {list.map((item, i) => (
            <ListElement
              hideImages={hideImages || this.state.stringArray}
              highlight={i === highlight}
              i={_.isString(item) ? { value: item } : item}
              key={item[mapping.text] + i || item + i}
              mapping={mapping}
              onSelect={t => this.handleSelect(t)}
            />
          ))}
        </div>
      );
    }
  }

  render() {
    const {
      autoFocus,
      fullWidth,
      hideClear,
      label,
      multiSelect,
      tabIndex,
      value,
      ...rest
    } = this.props;
    let { hasFocus, showList, textFieldValue, textFieldImage, errors } = this.state;
    const cl = [styles.holder];
    let chipsValue = this.state.stringArray ? convertDataSet(value) : value;
    const chipsList =
      (multiSelect &&
        value &&
        ChipsList.getMatchedChips(this.dataSet, chipsValue, this.getMapping())) ||
      new Array(0);
    let style = { width: '100%' };

    return (
      <div className={cl.join(' ')} ref={ref => (this.holder = ref)} style={fullWidth && style}>
        <TextField
          {...rest}
          label={multiSelect ? (chipsList.length && ' ') || label : label}
          ref={ref => (this.textField = ref)}
          leftComponent={
            multiSelect
              ? (chipsList.length && (
                <ChipsList onRemove={this.handleRemove} value={chipsList} />
              )) ||
                ''
              : renderIcon(textFieldImage, styles)
          }
          autoFocus={autoFocus}
          error={errors}
          noValidation
          className={styles.input}
          onBlur={() => this.handleBlur()}
          onFocus={() => this.handleFocus()}
          onChange={this.handleTextChange}
          onKeyDown={this.onKeyDown}
          value={textFieldValue}
          icon={
            !hideClear && !!textFieldValue ? (
              <Button icon={ICON_CLOSE} onClick={this.clear} tabIndex={tabIndex || -1} />
            ) : (
              multiSelect &&
              value && <Button icon={ICON_CLOSE} onClick={this.clear} tabIndex={tabIndex || -1} />
            )
          }
        />
        {hasFocus && showList && this.renderList()}
      </div>
    );
  }
}
